import jszip from 'jszip';

/**
 * Zip file utility class for extracting and processing theme zip files.
 */
export default class ThemeZipFile {
  /** @type {File | null} */
  file = null;

  /** @type {jszip | null} */
  zipInstance = null;

  basePath = '';

  /**
   * @param {object} file
   */
  constructor(file) {
    this.file = file;
  }

  /**
   * Initialize a zip instance from file data.
   * @returns {Promise<jszip>}
   */
  async init() {
    if (this.zipInstance) {
      return this.zipInstance;
    }

    this.zipInstance = await jszip.loadAsync(this.file);

    if (Object.keys(this.zipInstance.files)?.length <= 0) {
      throw new Error('Invalid or empty zip file');
    }

    this.basePath = this.getBasePath();

    return this.zipInstance;
  }

  /**
   * Get the base path of a zip archive in case it compressed the original folder by name.
   * @returns {string} the base path of the zip archive
   */
  getBasePath() {
    const firstKey = Object.keys(this.zipInstance.files)[0];
    const firstEntry = this.zipInstance.files[firstKey];
    const archiveFileName = String(this.file.name).replace(/\.zip$/, '');

    // If the first entry is a directory with the same name as the archive, use it as the base path
    if (firstEntry?.dir && firstEntry.name === `${archiveFileName}/`) {
      return firstEntry.name;
    }

    const swellJsonFound = this.findBaseFileByName('swell.json');
    if (swellJsonFound) {
      return swellJsonFound.path;
    }

    const shopifySettingsFound = this.findBaseFileByName(
      'config/settings_schema.json',
    );
    if (shopifySettingsFound) {
      return shopifySettingsFound.path;
    }

    // Otherwise try to find swell.json or config/settings_schema.json

    return '';
  }

  /**
   * Find a file entry by name in a zip archive.
   * @param {string} name
   * @returns {{ name: string, path: string | undefined } | undefined}
   */
  findBaseFileByName(name) {
    let foundName;
    let foundPath;

    for (const [entryName] of Object.entries(this.zipInstance.files)) {
      const fileName =
        entryName === name || entryName.endsWith(`/${name}`) ? name : undefined;
      const filePath = fileName ? fileName.replace(name, '') : undefined;
      if (fileName) {
        foundName = fileName;
        foundPath = filePath;
      }
    }

    return foundName ? { name: foundName, path: foundPath } : undefined;
  }

  /**
   * Extract a specific file from a zip archive.
   * @param {string} filePath
   * @returns {Promise<Buffer>} the file data
   */
  async getFileData(filePath) {
    await this.init();

    const zipEntry = this.zipInstance.file(`${this.basePath}${filePath}`);

    if (!zipEntry) {
      throw new Error(`File "${filePath}" not found in the zip archive`);
    }

    if (zipEntry.isDirectory) {
      throw new Error(`"${filePath}" is a directory, not a file`);
    }

    return getFileDataFromZipEntry(zipEntry);
  }

  /**
   * Extract and process each file from a zip instance.
   * @param {(filePath: string, fileData: Buffer) => Promise<void> | void} callback function to process each file
   * @returns {Promise<void>} resolves when all files are processed or rejects on an error.
   */
  async extractFiles(callback) {
    await this.init();

    // Iterate through each file in the zip
    await Promise.map(
      Object.entries(this.zipInstance.files),
      async ([_, zipEntry]) => {
        const fileData = await getFileDataFromZipEntry(zipEntry);
        const filePath = zipEntry.name.slice(this.basePath.length);
        await callback(filePath, fileData);
      },
      { concurrency: 1 },
    );
  }

  /**
   * Get swell configuration from a theme upload.
   * @returns {Promise<{ swellConfig: object, isGenerated: boolean }>}
   */
  async getSwellConfigFromUploadTheme() {
    let swellConfig;
    let isGenerated = false;

    try {
      // Try to get swell.json first
      const swellJson = await this.getFileData('swell.json');
      swellConfig = JSON.parse(swellJson.toString('utf8'));
    } catch (err) {
      console.log(err);
      // Try to create config from shopify theme if applicable
      swellConfig = await this.getSwellConfigFromShopifyThemeInfo();
      if (!swellConfig) {
        throw new Error(
          `Invalid theme configuration in swell.json: ${err.message}`,
        );
      }

      // Indicate it was a shopify theme without swell.json
      isGenerated = true;
    }

    return { swellConfig, isGenerated };
  }

  /**
   * Get swell.json from shopify theme info if possible.
   * @returns {Promise<object | null>}
   */
  async getSwellConfigFromShopifyThemeInfo() {
    try {
      const shopifySettingSchema = await this.getFileData(
        'config/settings_schema.json',
      );

      const config = JSON.parse(shopifySettingSchema.toString('utf8'));

      const shopifyThemeInfo =
        config?.[0]?.name === 'theme_info' ? config[0] : null;

      if (shopifyThemeInfo) {
        return {
          type: 'theme',
          id: _.snakeCase(shopifyThemeInfo.theme_name),
          name: shopifyThemeInfo.theme_name,
          version: shopifyThemeInfo.theme_version || '1.0.0',
          support_url: shopifyThemeInfo.theme_support_url,
          documentation_url: shopifyThemeInfo.theme_documentation_url,
          theme: {
            storefront: {
              compatibility: {
                shopify: true,
              },
            },
          },
        };
      }

      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Check if the theme is a Shopify theme.
   * @returns {Promise<boolean>}
   */
  async isShopifyTheme() {
    const { swellConfig } = await this.getSwellConfigFromUploadTheme();

    return Boolean(swellConfig?.theme?.storefront?.compatibility?.shopify);
  }

  /**
   * Check if the theme requires Shopify image import.
   * @returns {Promise<boolean>}
   */
  async requiresShopifyImageImport() {
    if (!(await this.isShopifyTheme())) {
      return false;
    }

    const shopifySettingData = await this.getFileData(
      'config/settings_data.json',
    );

    const templateSettings = [
      { filePath: 'config/settings_data.json', fileData: shopifySettingData },
    ];

    await this.extractFiles(async (filePath, fileData) => {
      if (filePath.startsWith('templates/') && filePath.endsWith('.json')) {
        templateSettings.push({ filePath, fileData });
      }
    });

    const hasPopulatedImageSettings = templateSettings.some(({ fileData }) => {
      // Look over nested object paths for something starting with shopify://shop_images/
      return String(fileData)
        .replace(/\\\//g, '/')
        .includes('"shopify://shop_images/');
    });

    return hasPopulatedImageSettings;
  }

  /**
   * Validate the theme.
   * @returns {Promise<string | null>} error message if validation fails, null otherwise
   */
  async validate() {
    if (await this.isShopifyTheme()) {
      // Do not allow Shopify 1.0 themes
      const indexLiquid = await this.getFileData('templates/index.liquid');
      if (indexLiquid) {
        return 'Shopify 1.0 themes are not yet supported.';
      }
    }

    return null;
  }
}

/**
 * @param {File.entry} zipEntry
 * @returns {Promise<Buffer>}
 */
function getFileDataFromZipEntry(zipEntry) {
  return new Promise((resolve, reject) => {
    zipEntry.async('string').then((data, err) => {
      err ? reject(err) : resolve(data);
    });
  });
}
